import { getExcursoesFromStorage } from '../../../shared/lib/excursaoStorage';
import { getCategorias, getExperiencias } from './experienciasStorage';
import { getGuias } from './guiasAgenciasStorage';

export type RankingPeriodo = 'mensal' | 'anual';

export interface GuiaRankingRow {
  guiaId: string;
  nome: string;
  diasTrabalhados: number;
  pessoasGuiadas: number;
  pontos: number;
  posicao: number;
}

export interface ExperienciaLeaderRow {
  experienciaId: number;
  experienciaNome: string;
  tipoRanking: 'terapia' | 'guia';
  primeiroGuiaId: string | null;
  primeiroNome: string;
  primeiroPontos: number;
  segundoNome: string;
  segundoPontos: number | null;
  terceiroNome: string;
  terceiroPontos: number | null;
  diasTrabalhados: number;
  pessoasGuiadas: number;
  pontos: number;
}

type RankingAccumulator = {
  dias: Set<string>;
  pessoas: number;
};

function normalizeLower(s: string): string {
  return s.toLowerCase().trim().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
}

function nascimentoSortKey(dataNascimento?: string): number {
  if (!dataNascimento) return Number.POSITIVE_INFINITY;
  const t = new Date(dataNascimento).getTime();
  return Number.isNaN(t) ? Number.POSITIVE_INFINITY : t;
}

function inPeriodo(dateIso: string, ref: Date, periodo: RankingPeriodo): boolean {
  if (!dateIso) return false;
  const [yearRaw, monthRaw] = dateIso.split('-');
  const year = Number(yearRaw);
  const month = Number(monthRaw);
  if (!year || !month) return false;
  if (periodo === 'anual') return year === ref.getFullYear();
  return year === ref.getFullYear() && month === ref.getMonth() + 1;
}

function resolveGuiaId(excGuiaId: string | undefined, excGuiaEmail: string | undefined, guias: Guia[]): string | null {
  if (excGuiaId && guias.some((g) => g.id === excGuiaId)) return excGuiaId;
  if (!excGuiaEmail) return null;
  const byEmail = guias.find((g) => g.email.trim().toLowerCase() === excGuiaEmail.trim().toLowerCase());
  return byEmail?.id ?? null;
}

function compararPorIdade(aGuiaId: string, bGuiaId: string, nascimentoByGuiaId: Map<string, string | undefined>): number {
  const aNascimento = nascimentoSortKey(nascimentoByGuiaId.get(aGuiaId));
  const bNascimento = nascimentoSortKey(nascimentoByGuiaId.get(bGuiaId));
  return aNascimento - bNascimento;
}

export function calcularPontosRanking(diasTrabalhados: number, pessoasGuiadas: number): number {
  // Peso estilo campeonato: dia trabalhado tem peso maior.
  return diasTrabalhados * 3 + pessoasGuiadas;
}

export function getGuiaRankingRows(periodo: RankingPeriodo, refDate = new Date()): GuiaRankingRow[] {
  const guias = getGuias();
  const nascimentoByGuiaId = new Map(guias.map((g) => [g.id, g.dataNascimento]));
  const excursoes = Object.values(getExcursoesFromStorage());
  const byGuia = new Map<string, RankingAccumulator>();

  excursoes.forEach((ex) => {
    if (ex.status === 'cancelada') return;
    if (!inPeriodo(ex.date, refDate, periodo)) return;

    const guiaId = resolveGuiaId(ex.guiaId, ex.guiaEmail, guias);
    if (!guiaId) return;

    const current = byGuia.get(guiaId) ?? { dias: new Set<string>(), pessoas: 0 };
    current.dias.add(ex.date);
    current.pessoas += Math.max(0, Number(ex.inscritos) || 0);
    byGuia.set(guiaId, current);
  });

  const rows = guias.map((g) => {
    const acc = byGuia.get(g.id);
    const diasTrabalhados = acc?.dias.size ?? 0;
    const pessoasGuiadas = acc?.pessoas ?? 0;
    const pontos = calcularPontosRanking(diasTrabalhados, pessoasGuiadas);
    return {
      guiaId: g.id,
      nome: g.nome,
      diasTrabalhados,
      pessoasGuiadas,
      pontos,
      posicao: 0,
    };
  });

  rows.sort((a, b) => {
    if (b.pontos !== a.pontos) return b.pontos - a.pontos;
    if (b.pessoasGuiadas !== a.pessoasGuiadas) return b.pessoasGuiadas - a.pessoasGuiadas;
    if (b.diasTrabalhados !== a.diasTrabalhados) return b.diasTrabalhados - a.diasTrabalhados;
    const byIdade = compararPorIdade(a.guiaId, b.guiaId, nascimentoByGuiaId);
    if (byIdade !== 0) return byIdade;
    return a.nome.localeCompare(b.nome, 'pt-BR');
  });

  return rows.map((row, idx) => ({ ...row, posicao: idx + 1 }));
}

export function getLideresPorExperiencia(periodo: RankingPeriodo, refDate = new Date()): ExperienciaLeaderRow[] {
  const guias = getGuias();
  const nascimentoByGuiaId = new Map(guias.map((g) => [g.id, g.dataNascimento]));
  const guiaById = new Map(guias.map((g) => [g.id, g]));

  const experiencias = getExperiencias();
  const categorias = getCategorias();
  const categoriaNomeById = new Map(categorias.map((c) => [c.id, normalizeLower(c.name)]));
  const tipoByExperienciaId = new Map<number, 'terapia' | 'guia'>(
    experiencias.map((exp, idx) => {
      const isTerapia = (exp.categoryIds ?? []).some((cid) => (categoriaNomeById.get(cid) ?? '').includes('terapia'));
      return [idx + 1, isTerapia ? 'terapia' : 'guia'];
    })
  );

  const excursoes = Object.values(getExcursoesFromStorage());
  const byExperiencia = new Map<number, Map<string, RankingAccumulator>>();

  excursoes.forEach((ex) => {
    if (ex.status === 'cancelada') return;
    if (!inPeriodo(ex.date, refDate, periodo)) return;
    const guiaId = resolveGuiaId(ex.guiaId, ex.guiaEmail, guias);
    if (!guiaId) return;

    const experienceIds = Array.isArray(ex.experiences) ? ex.experiences : [];
    experienceIds.forEach((expId) => {
      const expNumericId = Number(expId);
      if (!Number.isFinite(expNumericId) || expNumericId <= 0) return;

      const currentByGuia = byExperiencia.get(expNumericId) ?? new Map<string, RankingAccumulator>();
      const acc = currentByGuia.get(guiaId) ?? { dias: new Set<string>(), pessoas: 0 };
      acc.dias.add(ex.date);
      acc.pessoas += Math.max(0, Number(ex.inscritos) || 0);
      currentByGuia.set(guiaId, acc);
      byExperiencia.set(expNumericId, currentByGuia);
    });
  });

  return experiencias
    .map((exp, idx) => {
      const experienciaId = idx + 1;
      const tipoRanking = tipoByExperienciaId.get(experienciaId) ?? 'guia';
      const rankingGuiaMap = byExperiencia.get(experienciaId) ?? new Map<string, RankingAccumulator>();
      const candidatos = Array.from(rankingGuiaMap.entries()).map(([guiaId, acc]) => {
        const diasTrabalhados = acc.dias.size;
        const pessoasGuiadas = acc.pessoas;
        const pontos = tipoRanking === 'terapia' ? pessoasGuiadas : calcularPontosRanking(diasTrabalhados, pessoasGuiadas);
        return { guiaId, diasTrabalhados, pessoasGuiadas, pontos };
      });

      candidatos.sort((a, b) => {
        if (tipoRanking === 'terapia') {
          if (b.pessoasGuiadas !== a.pessoasGuiadas) return b.pessoasGuiadas - a.pessoasGuiadas;
          if (b.diasTrabalhados !== a.diasTrabalhados) return b.diasTrabalhados - a.diasTrabalhados;
        } else {
          if (b.pontos !== a.pontos) return b.pontos - a.pontos;
          if (b.pessoasGuiadas !== a.pessoasGuiadas) return b.pessoasGuiadas - a.pessoasGuiadas;
          if (b.diasTrabalhados !== a.diasTrabalhados) return b.diasTrabalhados - a.diasTrabalhados;
        }
        const byIdade = compararPorIdade(a.guiaId, b.guiaId, nascimentoByGuiaId);
        if (byIdade !== 0) return byIdade;
        return (guiaById.get(a.guiaId)?.nome ?? a.guiaId).localeCompare(
          guiaById.get(b.guiaId)?.nome ?? b.guiaId,
          'pt-BR'
        );
      });

      const lider = candidatos[0];
      const primeiro = candidatos[0];
      const segundo = candidatos[1];
      const terceiro = candidatos[2];
      return {
        experienciaId,
        experienciaNome: exp.titulo || `Experiência ${experienciaId}`,
        tipoRanking,
        primeiroGuiaId: primeiro?.guiaId ?? null,
        primeiroNome: primeiro ? (guiaById.get(primeiro.guiaId)?.nome ?? primeiro.guiaId) : 'Sem líder',
        primeiroPontos: primeiro?.pontos ?? 0,
        segundoNome: segundo ? (guiaById.get(segundo.guiaId)?.nome ?? segundo.guiaId) : '—',
        segundoPontos: segundo?.pontos ?? null,
        terceiroNome: terceiro ? (guiaById.get(terceiro.guiaId)?.nome ?? terceiro.guiaId) : '—',
        terceiroPontos: terceiro?.pontos ?? null,
        diasTrabalhados: lider?.diasTrabalhados ?? 0,
        pessoasGuiadas: lider?.pessoasGuiadas ?? 0,
        pontos: lider?.pontos ?? 0,
      } satisfies ExperienciaLeaderRow;
    })
    .sort((a, b) => a.experienciaNome.localeCompare(b.experienciaNome, 'pt-BR'));
}
